import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Send,
  Bot,
  User,
  Sparkles,
  BarChart3,
  Home,
  Users,
  DollarSign,
  TrendingUp,
  AlertCircle,
  RotateCcw,
  LogOut,
  ArrowUp,
  Copy,
  FileText,
  Check
} from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { useBusinessAI } from '../hooks/useBusinessAI';
import { Usuario } from '../hooks/useUsuarios';
import PublicChatLogin from './PublicChatLogin';
import { Toast } from './ui/Toast';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  vendorBlocks?: { [key: string]: string };
}

// Componente público del chat para acceso con autenticación de socios
export function PublicChatIA() {
  const [usuario, setUsuario] = useState<Usuario | null>(() => {
    // Intentar recuperar usuario autenticado desde localStorage
    const savedUser = localStorage.getItem('bimobiChatUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline' | 'loading'>('loading');
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({ show: false, message: '', type: 'success' });
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { lots, expenses, sellers, loading, error, usuarios } = useAppData();
  const { processBusinessQuery, businessStats } = useBusinessAI();

  // Validar que el usuario siga siendo válido
  useEffect(() => {
    if (usuario && usuarios && usuarios.length > 0) {
      const usuarioValido = usuarios.find((u: Usuario) =>
        u.id === usuario.id &&
        u.tipo === 'socio' &&
        u.activo &&
        !u.bloqueado
      );
      if (!usuarioValido) {
        handleLogout();
      }
    }
  }, [usuario, usuarios]);

  // Función para manejar login exitoso
  const handleLoginSuccess = (usuarioAutenticado: Usuario) => {
    setUsuario(usuarioAutenticado);
    localStorage.setItem('bimobiChatUser', JSON.stringify(usuarioAutenticado));
  };

  // Función para logout
  const handleLogout = () => {
    setUsuario(null);
    localStorage.removeItem('bimobiChatUser');
    setMessages([]);
  };

  // Mensaje de bienvenida personalizado
  const welcomeMessage = useMemo(() => ({
    id: '1',
    role: 'assistant' as const,
    content: `¡Hola${usuario ? ` **${usuario.nombre}**` : ''}! 👋 Soy tu **Asistente Bimobi** 🏠💼\n\n🔍 **BÚSQUEDA SIMPLE - Solo escribe:**\n\n👤 **Nombre del cliente** o 🏠 **Número del lote**\n\n**Ejemplo:** Juan Ramos o Lote 54`,
    timestamp: new Date(),
    suggestions: [
      'Fecha de pago Lote',
      'Informe financiero',
      'Vencidas por vendedor'
    ]
  }), [businessStats, usuario]);

  // Monitoreo de estado de datos
  useEffect(() => {
    if (loading) {
      setConnectionStatus('loading');
    } else if (error) {
      setConnectionStatus('offline');
    } else if (businessStats) {
      setConnectionStatus('online');
    }
  }, [loading, error, businessStats]);

  // Inicializar con chat vacío
  useEffect(() => {
    if (messages.length === 0 && connectionStatus === 'online') {
      setMessages([]);
    }
  }, [messages.length, connectionStatus]);

  // Auto scroll
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Detección de teclado móvil
  useEffect(() => {
    const handleResize = () => {
      const initialHeight = window.visualViewport?.height || window.innerHeight;
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const heightDifference = initialHeight - currentHeight;

      // Si la altura se reduce más de 150px, asumimos que el teclado está abierto
      setIsKeyboardOpen(heightDifference > 150);
    };

    const handleFocus = () => {
      // En móvil, cuando se enfoca el input, es probable que se abra el teclado
      if (window.innerWidth <= 768) {
        setTimeout(() => setIsKeyboardOpen(true), 300);
      }
    };

    const handleBlur = () => {
      setTimeout(() => setIsKeyboardOpen(false), 300);
    };

    // Eventos de viewport para detección de teclado
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // Eventos del input
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }

      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);



  // Procesamiento de consultas
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    if (connectionStatus === 'offline') {
      const offlineMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '🔌 **Sin conexión**\n\nActualmente no puedo acceder a los datos del negocio.\n\n🔄 **Intenta:**\n• Recargar la página\n• Verificar tu conexión a internet\n• Contactar al administrador si persiste',
        timestamp: new Date(),
        suggestions: ['Recargar página', 'Verificar conexión']
      };
      setMessages(prev => [...prev, offlineMessage]);
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    try {
      const result = processBusinessQuery(currentInput);

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `${result.answer}\n\n📊 **Datos actualizados** ${new Date().toLocaleTimeString('es-GT')}`,
        timestamp: new Date(),
        suggestions: result.suggestions,
        vendorBlocks: result.vendorBlocks
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error procesando consulta:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '❌ **Error procesando consulta**\n\nHubo un problema técnico. Por favor:\n• Intenta reformular tu pregunta\n• Verifica que el nombre del cliente esté correcto\n• Recarga la página si persiste',
        timestamp: new Date(),
        suggestions: [
          '¿Cuántos lotes están disponibles?',
          'Recargar página',
          'Intentar otra consulta'
        ]
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
    inputRef.current?.focus();
  };

  // Funciones mejoradas de copia y compartir
  const copyMessage = async (content: string, messageId: string, format: 'plain' | 'formatted' | 'whatsapp' = 'plain') => {
    try {
      let textToCopy = content;

      if (format === 'plain') {
        textToCopy = content
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .replace(/<br\s*\/?>/g, '\n')
          .replace(/<[^>]*>/g, '')
          .trim();
      } else if (format === 'whatsapp') {
        textToCopy = content
          .replace(/\*\*(.*?)\*\*/g, '*$1*')
          .replace(/\*(.*?)\*/g, '_$1_')
          .replace(/<br\s*\/?>/g, '\n')
          .replace(/<[^>]*>/g, '')
          .trim();
      }

      await navigator.clipboard.writeText(textToCopy);
      setCopiedMessageId(messageId);
      setToast({ show: true, message: 'Copiado con formato', type: 'success' });
      setTimeout(() => setCopiedMessageId(null), 2000);

    } catch (error) {
      console.error('Error al copiar:', error);
      const textArea = document.createElement('textarea');
      textArea.value = content.replace(/<[^>]*>/g, '').replace(/\*\*(.*?)\*\*/g, '$1');
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedMessageId(messageId);
      setToast({ show: true, message: 'Copiado al portapapeles', type: 'success' });
      setTimeout(() => setCopiedMessageId(null), 2000);
    }
  };



  const copyAllChat = () => {
    const allMessages = messages
      .map(msg => {
        const role = msg.role === 'user' ? '👤 Usuario' : '🤖 Asistente';
        const time = msg.timestamp.toLocaleString('es-GT');
        const content = msg.content
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .replace(/<br\s*\/?>/g, '\n')
          .replace(/<[^>]*>/g, '')
          .trim();
        return `${role} (${time}):\n${content}`;
      })
      .join('\n\n---\n\n');

    const fullChat = `🏠 MILTIERRAS - HISTORIAL DE CHAT\n\nGenerado: ${new Date().toLocaleString('es-GT')}\n\n${allMessages}`;

    navigator.clipboard.writeText(fullChat);
    setCopiedMessageId('all-chat');
    setToast({ show: true, message: 'Chat completo copiado', type: 'success' });
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const formatMessage = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  };

  // Loading state
  if (connectionStatus === 'loading') {
    return (
      <div className="flex flex-col h-screen max-w-4xl mx-auto bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Cargando Asistente Bimobi...</h2>
          <p className="text-gray-500 mt-2">Conectando con la base de datos</p>
        </div>
      </div>
    );
  }

  // Authentication required - show login if user is not authenticated
  if (!usuario) {
    return <PublicChatLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className={`flex flex-col h-screen max-w-4xl mx-auto bg-white shadow-xl border border-gray-200 ${isKeyboardOpen ? 'mobile-keyboard-open' : ''}`}>
      {/* Header simplificado estilo Grok */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center">
          <h1 className="text-xl font-semibold text-gray-900">Bimobi</h1>
        </div>

        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copyAllChat}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl"
              title="Copiar todo el chat"
            >
              {copiedMessageId === 'all-chat' ? (
                <Check className="h-4 w-4 mr-1" />
              ) : (
                <FileText className="h-4 w-4 mr-1" />
              )}
              {copiedMessageId === 'all-chat' ? 'Copiado' : 'Todo'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMessages([])}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl"
            title="Nuevo chat"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Nuevo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
            title={`Cerrar sesión (${usuario.nombre})`}
          >
            <LogOut className="h-4 w-4 mr-1" />
            Salir
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4 bg-gray-50/30 overflow-y-auto max-h-[calc(100vh-16rem)] scroll-area">
        <div className="space-y-4 min-h-full">


          {messages.map((message, index) => (
            <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {message.role === 'assistant' && (
                <div className="flex-shrink-0">
                  <div className="p-2 bg-white rounded-xl border border-gray-200">
                    <Bot className="h-4 w-4 text-gray-700" />
                  </div>
                </div>
              )}

              <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                <div
                  className={`p-3 rounded-xl ${message.role === 'user'
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                >
                  <div
                    className="text-sm"
                    dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                  />

                  {message.role === 'assistant' && (
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyMessage(message.content, message.id, 'whatsapp')}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${copiedMessageId === message.id
                              ? 'text-green-600 bg-green-50'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                          title="Copiar con formato"
                        >
                          {copiedMessageId === message.id ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                          {copiedMessageId === message.id ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>

                      <span className="text-xs text-gray-400">
                        {message.timestamp.toLocaleTimeString('es-GT', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}

                  {/* Botones de copia por vendedor para móvil */}
                  {message.role === 'assistant' && message.vendorBlocks && Object.keys(message.vendorBlocks).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="mb-2">
                        <span className="text-xs font-medium text-gray-600">📋 Copiar por vendedor:</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(message.vendorBlocks).map(([vendedor, bloque]) => (
                          <button
                            key={vendedor}
                            onClick={() => {
                              copyMessage(bloque, `${message.id}-${vendedor}`, 'whatsapp');
                            }}
                            className={`text-xs px-2 py-2 rounded-lg border transition-all duration-200 text-center ${copiedMessageId === `${message.id}-${vendedor}`
                                ? 'bg-green-50 border-green-200 text-green-700'
                                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            {copiedMessageId === `${message.id}-${vendedor}` ? (
                              <div className="flex items-center justify-center gap-1">
                                <Check className="h-3 w-3" />
                                <span>Copiado</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <Copy className="h-3 w-3" />
                                <span className="truncate">{vendedor}</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-gray-500 text-center">
                        📱 Copiado con formato optimizado
                      </div>
                    </div>
                  )}
                </div>

                {/* Suggestions */}

              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0">
                  <div className="p-2 bg-white rounded-xl border border-gray-200">
                    <User className="h-4 w-4 text-gray-700" />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0">
                <div className="p-2 bg-white rounded-xl border border-gray-200">
                  <Bot className="h-4 w-4 text-gray-700" />
                </div>
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-600">Consultando datos...</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </ScrollArea>


      {/* Acciones rápidas arriba del input estilo Grok */}
      {!inputMessage.trim() && !isKeyboardOpen && (
        <div className="px-4 pb-2">
          <div className="flex gap-3 overflow-x-auto justify-center">
            {[
              { icon: DollarSign, text: 'Cobros mes', query: 'Cobros del mes por vendedor' },
              { icon: TrendingUp, text: 'Informe financiero', query: 'Informe financiero' },
              { icon: AlertCircle, text: 'Vencidas', query: 'Vencidas por vendedor' }
            ].map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestionClick(action.query)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-white hover:bg-gray-100 rounded-xl border border-gray-300 hover:border-gray-400 whitespace-nowrap transition-all duration-200 text-gray-700 hover:text-gray-900 shadow-sm"
              >
                <action.icon className="h-4 w-4" />
                {action.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area estilo Grok - modo claro con más altura */}
      <div className="p-4 input-area bg-white">
        <div className="flex items-center gap-3 bg-gray-100 rounded-2xl p-4">
          {/* Input Field */}
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ingresa el número del lote o nombre comprador"
              className="w-full bg-transparent text-gray-900 placeholder:text-gray-400 focus:outline-none text-base py-2"
              disabled={isLoading || connectionStatus === 'offline'}
            />
          </div>

          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || connectionStatus === 'offline'}
            className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUp className="w-5 h-5 text-white" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          🤖 Asistente Bimobi • Consultas de negocio • Datos en tiempo real • **${new Date().toLocaleString('es-GT')}**
        </p>
      </div>

      {/* Toast de notificaciones */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
}
